/* =====================================================================
   RVIVL — COMING SOON: Klaviyo capture + SMS opt-in
   Vanilla ES2020, no dependencies.

   Flow:
     Step 1 (email)  → POST Klaviyo client subscription (email list)
                       → if SMS enabled: reveal Step 2, else show success
     Step 2 (SMS)    → validate phone (E.164) + require TCPA checkbox
                       → POST Klaviyo client subscription (SMS list)
                       → show success
     Skip            → show success directly

   Klaviyo v3 client endpoint (public, no auth header — company_id = public key):
     POST https://a.klaviyo.com/client/subscriptions/?company_id={PUBLIC_KEY}
     Headers: Content-Type: application/json, revision: <date>

   Element lookups are class/type based and form-scoped, so multiple
   instances of the section on one page work without id collisions.
   ===================================================================== */

(function () {
  'use strict';

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2026-04-15'; // current default; bump before retirement (~2yr window)
  var CUSTOM_SOURCE = 'Coming Soon Page';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* ---- Phone → E.164 (US) ---- */
  function formatE164(raw) {
    var digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits[0] === '1') return '+' + digits;
    return null; // invalid
  }

  /* ---- Klaviyo payload builder (v3 shape) ---- */
  function subscriptionPayload(opts) {
    var attributes = { email: opts.email };
    var subs = {};
    if (opts.emailConsent) subs.email = { marketing: { consent: 'SUBSCRIBED' } };
    if (opts.phone) {
      attributes.phone_number = opts.phone;
      subs.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }
    if (Object.keys(subs).length) attributes.subscriptions = subs;

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: CUSTOM_SOURCE,
          profile: { data: { type: 'profile', attributes: attributes } }
        }
      }
    };
    if (opts.listId) {
      body.data.relationships = { list: { data: { type: 'list', id: opts.listId } } };
    }
    return body;
  }

  async function klaviyoSubscribe(companyId, payload) {
    var url = KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId);
    var res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      var detail = 'HTTP ' + res.status;
      try {
        var json = await res.json();
        if (json && json.errors && json.errors[0] && json.errors[0].detail) {
          detail = json.errors[0].detail;
        }
      } catch (e) { /* empty / non-JSON body */ }
      throw new Error(detail);
    }
    return res;
  }

  /* ---- Small DOM helpers ---- */
  function showError(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
  }
  function clearError(el) {
    if (!el) return;
    el.textContent = '';
    el.classList.remove('is-visible');
  }
  function setBusy(btn, busy) {
    if (!btn) return;
    if (busy) {
      btn.dataset.label = btn.textContent;
      btn.style.minWidth = btn.offsetWidth + 'px'; // lock width so it can't shrink
      var loader = document.createElement('span');
      loader.className = 'cs-loader';
      loader.setAttribute('aria-hidden', 'true');
      btn.textContent = '';
      btn.appendChild(loader);
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
    } else {
      if (btn.dataset.label) btn.textContent = btn.dataset.label;
      btn.style.minWidth = '';
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  }
  function isBusy(btn) {
    return btn && btn.getAttribute('aria-busy') === 'true';
  }

  /* ---- Per-form controller ---- */
  function initForm(form) {
    var cfg = {
      companyId: form.getAttribute('data-klaviyo-key') || '',
      listId: form.getAttribute('data-list-id') || '',
      smsListId: form.getAttribute('data-sms-list-id') || '',
      showSms: form.getAttribute('data-show-sms') === 'true'
    };
    var msg = {
      invalidEmail: form.getAttribute('data-msg-invalid-email') || 'Please enter a valid email address.',
      invalidPhone: form.getAttribute('data-msg-invalid-phone') || 'Please enter a valid US phone number.',
      generic: form.getAttribute('data-msg-generic') || 'Something went wrong. Please try again.',
      success: form.getAttribute('data-msg-success') || "You're on the list."
    };

    var emailStep = form.querySelector('.cs-form__step-email');
    var emailInput = form.querySelector('input[type="email"]');
    var emailBtn = emailStep ? emailStep.querySelector('.cs-form__submit') : null;
    var emailError = emailStep ? emailStep.querySelector('.cs-form__error') : null;

    var smsStep = form.querySelector('.cs-form__sms-step');
    var phoneInput = form.querySelector('input[type="tel"]');
    var smsBtn = smsStep ? smsStep.querySelector('.cs-form__sms-submit') : null;
    var smsError = smsStep ? smsStep.querySelector('.cs-form__error') : null;
    var skipBtn = smsStep ? smsStep.querySelector('.cs-form__skip') : null;

    var successEl = form.querySelector('.cs-form__success');
    var capturedEmail = '';

    function showSmsStep() {
      if (!smsStep) { showSuccess(); return; }
      form.classList.add('is-sms'); // email already subscribed; hide the email row
      smsStep.classList.add('is-visible');
      smsStep.setAttribute('aria-hidden', 'false');
      // Next frame → opacity transition (disabled under prefers-reduced-motion).
      requestAnimationFrame(function () { smsStep.classList.add('is-shown'); });
      if (phoneInput) phoneInput.focus();
    }

    function showSuccess() {
      form.classList.add('is-success');
      if (!successEl) return;
      successEl.setAttribute('tabindex', '-1');
      // Inject text AFTER the region is visible so the polite live region
      // announces it, and move focus to the confirmation.
      requestAnimationFrame(function () {
        successEl.textContent = msg.success;
        successEl.focus();
      });
    }

    /* Step 1 — email */
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (isBusy(emailBtn)) return;
      clearError(emailError);

      var email = (emailInput && emailInput.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        showError(emailError, msg.invalidEmail);
        if (emailInput) emailInput.focus();
        return;
      }
      if (!cfg.companyId || !cfg.listId) {
        showError(emailError, msg.generic);
        console.warn('[coming-soon] Klaviyo email list not configured (public key or list id missing).');
        return;
      }

      setBusy(emailBtn, true);
      var done = false;
      try {
        await klaviyoSubscribe(cfg.companyId, subscriptionPayload({
          email: email, listId: cfg.listId, emailConsent: true
        }));
        capturedEmail = email;
        done = true;
        if (cfg.showSms && smsStep) { showSmsStep(); } else { showSuccess(); }
      } catch (err) {
        showError(emailError, msg.generic);
        console.error('[coming-soon] Klaviyo email subscribe failed:', err.message);
      } finally {
        setBusy(emailBtn, false);
        if (done) {
          if (emailInput) emailInput.disabled = true;
          if (emailBtn) emailBtn.disabled = true;
        }
      }
    });

    /* Step 2 — SMS */
    if (smsBtn) {
      smsBtn.addEventListener('click', async function () {
        if (isBusy(smsBtn)) return;
        clearError(smsError);

        var e164 = formatE164(phoneInput && phoneInput.value);
        if (!e164) {
          showError(smsError, msg.invalidPhone);
          if (phoneInput) phoneInput.focus();
          return;
        }
        if (!cfg.companyId || !cfg.smsListId) {
          showError(smsError, msg.generic);
          console.warn('[coming-soon] Klaviyo SMS list not configured (public key or SMS list id missing).');
          return;
        }

        setBusy(smsBtn, true);
        try {
          await klaviyoSubscribe(cfg.companyId, subscriptionPayload({
            email: capturedEmail, phone: e164, listId: cfg.smsListId
          }));
          showSuccess();
        } catch (err) {
          showError(smsError, msg.generic);
          console.error('[coming-soon] Klaviyo SMS subscribe failed:', err.message);
        } finally {
          setBusy(smsBtn, false);
        }
      });
    }

    /* Enter inside the phone field triggers SMS submit, not a form re-submit. */
    if (phoneInput && smsBtn) {
      phoneInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); smsBtn.click(); }
      });
    }

    /* Skip SMS */
    if (skipBtn) {
      skipBtn.addEventListener('click', showSuccess);
    }
  }

  function init() {
    var forms = document.querySelectorAll('.cs-form');
    for (var i = 0; i < forms.length; i++) initForm(forms[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
